import React, { useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { Formik } from 'formik';

import { Modal, ModalBodyFooter, Button, ModalLoading, ModalProcessing, ErrorMessage } from '../../custom-essentials';
import { SelectSingle, SelectMulti, FormikControl, DatePicker } from '../../form';
import {
    validationSchema, tagOptions, getInitTagOption, getInitStudentOption, getInitUserOption,
    getInitCenterOption, getCenterName, statusOptions, getInitStatusOption, renderMeta,
    priorityOptions, getInitPriorityOption, kanbanStatusOptions, getInitKanbanStatusOption,
    getInitProjectOption, formatDateForInput
} from './helpers';
import { formatDate, formatDateApi } from '../../functions';

// Detect portal from domain (must be after imports)
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isReadingPlex = hostname.includes('readingplex');

const initDate = new Date();
initDate.setHours(0, 0, 0, 0);

function FlagsBodyFooter(props) {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });

    const { mode, selectedFlag, studentOptions, userOptions, centerOptions, projectOptions, employeeOptions, loaded, submitted, handleClose, handleSubmit } = props;

    if (!loaded) {
        return (
            <ModalBodyFooter>
                <Modal.Body>
                    <ModalLoading />
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        color="lte-mpLRed"
                        onClick={handleClose}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </ModalBodyFooter>
        );
    } else if (submitted) {
        return (
            <ModalBodyFooter>
                <Modal.Body>
                    <h4>
                        Updated successfully!
                    </h4>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        color="lte-mpLRed"
                        onClick={handleClose}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </ModalBodyFooter>
        );
    } else if (mode === 'delete') {
        return (
            <Formik
                enableReinitialize
                initialValues={{}}
                validateOnChange={true}
                onSubmit={handleSubmit}
            >
                {formik => (
                    <ModalBodyFooter>
                        <Modal.Body>
                            <h4>Delete this flag?</h4>
                            <div className="grid grid-cols-1">
                                {isReadingPlex ? (
                                    <>
                                        <div>User: {selectedFlag.userName || `Unknown user (UID ${selectedFlag.user})`}</div>
                                        <div>Center: {getCenterName(selectedFlag.center, centerOptions)}</div>
                                        <div>Priority: {selectedFlag.priority || 'Not set'}</div>
                                        <div>Status: {selectedFlag.status}</div>
                                        <div>Created: {formatDate(selectedFlag.date_created)}</div>
                                    </>
                                ) : (
                                    <>
                                        <div>Student: {selectedFlag.studentName}</div>
                                        <div>Center: {getCenterName(selectedFlag.center, centerOptions)}</div>
                                        <div>Tag: {selectedFlag.tag}</div>
                                        <div>Status: {selectedFlag.status}</div>
                                        <div>Created: {formatDate(selectedFlag.date_created)}</div>
                                    </>
                                )}
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <div className="flex flex-row gap-x-2 flex-wrap">
                                {formik.isSubmitting &&
                                    <ModalProcessing />
                                }
                                {formik.status && !formik.isSubmitting ?
                                    <div className="text-mpLRed text-xs">
                                        {formik.status}
                                    </div> : null
                                }
                                <Button
                                    color="lte-mpLRed"
                                    disabled={formik.isSubmitting}
                                    onClick={handleClose}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    color="hol-mpLRed"
                                    disabled={formik.isSubmitting}
                                    onClick={formik.handleSubmit}
                                >
                                    Delete
                                </Button>
                            </div>
                        </Modal.Footer>
                    </ModalBodyFooter>
                )}
            </Formik>
        );
    } else {
        // Determine initial values based on portal
        // Use empty arrays as fallback to prevent errors
        const safeUserOptions = (userOptions && userOptions.length > 0) ? userOptions : (studentOptions && studentOptions.length > 0 ? studentOptions : []);
        const safeStudentOptions = (studentOptions && studentOptions.length > 0) ? studentOptions : [];
        const safeCenterOptions = (centerOptions && centerOptions.length > 0) ? centerOptions : [];
        const safeEmployeeOptions = (employeeOptions && employeeOptions.length > 0) ? employeeOptions : [];
        
        // Log options for debugging
        console.log('[FLAGS BF] Options received:', {
            userOptions: userOptions?.length || 0,
            studentOptions: studentOptions?.length || 0,
            centerOptions: centerOptions?.length || 0,
            employeeOptions: employeeOptions?.length || 0,
            safeUserOptions: safeUserOptions.length,
            safeCenterOptions: safeCenterOptions.length
        });

        // Ensure selectedFlag is an object (not null/undefined)
        const flag = selectedFlag || {};

        const getInitialValues = () => {
            if (isReadingPlex) {
                // Parse assigned_employees JSON if it exists
                let assignedEmployeesList = [];
                if (flag.assigned_employees) {
                    try {
                        const parsed = typeof flag.assigned_employees === 'string'
                            ? JSON.parse(flag.assigned_employees)
                            : flag.assigned_employees;
                        if (Array.isArray(parsed) && employeeOptions && employeeOptions.length > 0) {
                            assignedEmployeesList = parsed.map(empId =>
                                employeeOptions.find(e => e.value === empId) || { value: empId, label: `Unknown (ID: ${empId})` }
                            ).filter(e => e);
                        }
                    } catch (e) {
                        console.error('[FLAGS] Error parsing assigned_employees:', e);
                    }
                }

                // Parse students JSON if it exists
                let studentsList = [];
                if (flag.students) {
                    try {
                        const parsed = typeof flag.students === 'string'
                            ? JSON.parse(flag.students)
                            : flag.students;
                        if (Array.isArray(parsed) && safeStudentOptions && safeStudentOptions.length > 0) {
                            studentsList = parsed.map(studentId =>
                                safeStudentOptions.find(s => s.value === studentId) || { value: studentId, label: `Unknown (ID: ${studentId})` }
                            ).filter(s => s);
                        }
                    } catch (e) {
                        console.error('[FLAGS] Error parsing students:', e);
                    }
                }

                return {
                    user: getInitUserOption(flag.user, safeUserOptions),
                    center: getInitCenterOption(flag.center, safeCenterOptions),
                    priority: getInitPriorityOption(flag.priority || 'Low'), // Default to 'Low'
                    notes: flag.notes || '',
                    followupNotes: flag.followup_notes || '',
                    status: getInitStatusOption(flag.status || flag.kanban_status), // Use kanban_status if status not set
                    // Kanban fields
                    projectName: flag.project_name || '', // Use project_name text field instead of project dropdown
                    taskName: flag.task_name || '',
                    dueDate: formatDateForInput(flag.due_date),
                    assignedEmployees: assignedEmployeesList,
                    students: studentsList, // Students field (optional, multi-select)
                    kanbanStatus: getInitKanbanStatusOption(flag.kanban_status || flag.status) // Use status if kanban_status not set
                };
            } else {
                return {
                    student: getInitStudentOption(flag.student, safeStudentOptions),
                    tag: getInitTagOption(flag.tag),
                    center: getInitCenterOption(flag.center, safeCenterOptions),
                    additionalNotes: flag.notes || '',
                    adminNotes: flag.admin_notes || '',
                    status: getInitStatusOption(flag.status)
                };
            }
        };

        return (
            <Formik
                enableReinitialize
                initialValues={getInitialValues()}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {formik => (
                    <ModalBodyFooter>
                        <Modal.Body>
                            {isReadingPlex ? (
                                // ReadingPlex form fields
                                <>
                                    <div className="flex flex-row gap-x-4">
                                        <div className="grid grid-cols-1 gap-y-2 w-full">
                                            <h4>User</h4>
                                            <SelectSingle
                                                id="flags-user"
                                                name="user"
                                                value={formik.values.user}
                                                onChange={(e) => {
                                                    const u = e.target.value;
                                                    const newValues = { ...formik.values };
                                                    newValues.user = u;
                                                    // Auto-set center from user's primary center if available
                                                    const userCenter = u.obj?.rp_primary_center;
                                                    if (userCenter) {
                                                        newValues.center = getInitCenterOption(userCenter, safeCenterOptions);
                                                    }
                                                    formik.setValues(newValues);
                                                }}
                                                options={safeUserOptions.length > 0 ? safeUserOptions : [{ value: -1, label: 'Loading users...' }]}
                                            />
                                            {formik.errors.user ? (
                                                <ErrorMessage color="mpLRed">
                                                    {formik.errors.user}
                                                </ErrorMessage>
                                            ) : null}
                                        </div>
                                    </div>

                                    <br />

                                    <div className="flex flex-row gap-x-4 items-start">
                                        <div className="grid grid-cols-1 gap-y-2 w-1/2">
                                            <h4>Priority</h4>
                                            <SelectSingle
                                                id="flags-priority"
                                                name="priority"
                                                value={formik.values.priority}
                                                onChange={formik.handleChange}
                                                options={priorityOptions}
                                            />
                                            {formik.errors.priority ? (
                                                <ErrorMessage color="mpLRed">
                                                    {formik.errors.priority}
                                                </ErrorMessage>
                                            ) : null}
                                        </div>
                                        <div className="grid grid-cols-1 gap-y-2 w-1/2">
                                            <h4>Center</h4>
                                            <SelectSingle
                                                id="flags-center"
                                                name="center"
                                                value={formik.values.center}
                                                onChange={formik.handleChange}
                                                options={safeCenterOptions.length > 0 ? safeCenterOptions : [{ value: -1, label: 'Loading centers...' }]}
                                            />
                                            {formik.errors.center ? (
                                                <ErrorMessage color="mpLRed">
                                                    {formik.errors.center}
                                                </ErrorMessage>
                                            ) : null}
                                        </div>
                                    </div>

                                    <br />

                                    {/* Kanban Fields Section */}
                                    <div className="flex flex-row gap-x-4 items-start">
                                        <div className="grid grid-cols-1 gap-y-2 w-1/2">
                                            <h4>Project Name (Optional)</h4>
                                            <FormikControl
                                                id="flags-project-name"
                                                name="projectName"
                                                value={formik.values.projectName}
                                                onChange={formik.handleChange}
                                                placeholder="Enter project name (optional, max 200 characters)"
                                            />
                                            {formik.errors.projectName ? (
                                                <ErrorMessage color="mpLRed">
                                                    {formik.errors.projectName}
                                                </ErrorMessage>
                                            ) : null}
                                        </div>
                                        <div className="grid grid-cols-1 gap-y-2 w-1/2">
                                            <h4>Status</h4>
                                            <SelectSingle
                                                id="flags-status"
                                                name="status"
                                                value={formik.values.status}
                                                onChange={(e) => {
                                                    // Sync kanbanStatus with status
                                                    const newStatus = e.target.value;
                                                    formik.setFieldValue('status', newStatus);
                                                    formik.setFieldValue('kanbanStatus', newStatus);
                                                }}
                                                options={statusOptions}
                                            />
                                            {formik.errors.status ? (
                                                <ErrorMessage color="mpLRed">
                                                    {formik.errors.status}
                                                </ErrorMessage>
                                            ) : null}
                                        </div>
                                    </div>

                                    <br />

                                    <div className="flex flex-row gap-x-4">
                                        <div className="grid grid-cols-1 gap-y-2 w-full">
                                            <h4>Task Name (Optional)</h4>
                                            <FormikControl
                                                id="flags-task-name"
                                                name="taskName"
                                                value={formik.values.taskName}
                                                onChange={formik.handleChange}
                                                placeholder="Enter task name (optional, max 200 characters). If left empty, notes will be used."
                                            />
                                            {formik.errors.taskName ? (
                                                <ErrorMessage color="mpLRed">
                                                    {formik.errors.taskName}
                                                </ErrorMessage>
                                            ) : null}
                                        </div>
                                    </div>

                                    <br />

                                    <div className="flex flex-row gap-x-4 items-start">
                                        <div className="grid grid-cols-1 gap-y-2 w-1/2">
                                            <h4>Due Date (Optional)</h4>
                                            <DatePicker
                                                id="flags-due-date"
                                                name="dueDate"
                                                value={formik.values.dueDate}
                                                onChange={formik.handleChange}
                                                classNames="w-full"
                                            />
                                            {formik.errors.dueDate ? (
                                                <ErrorMessage color="mpLRed">
                                                    {formik.errors.dueDate}
                                                </ErrorMessage>
                                            ) : null}
                                        </div>
                                        <div className="grid grid-cols-1 gap-y-2 w-1/2">
                                            <h4>Assigned Employees (Optional)</h4>
                                            <SelectMulti
                                                id="flags-assigned-employees"
                                                name="assignedEmployees"
                                                value={formik.values.assignedEmployees || []}
                                                onChange={formik.handleChange}
                                                options={safeEmployeeOptions.length > 0 ? safeEmployeeOptions : [{ value: -1, label: 'Loading employees...' }]}
                                                placeholder="Select employees..."
                                                closeMenuOnSelect={false}
                                            />
                                            {formik.errors.assignedEmployees ? (
                                                <ErrorMessage color="mpLRed">
                                                    {formik.errors.assignedEmployees}
                                                </ErrorMessage>
                                            ) : null}
                                        </div>
                                    </div>

                                    <br />

                                    <div className="flex flex-row gap-x-4">
                                        <div className="grid grid-cols-1 gap-y-2 w-full">
                                            <h4>Students (Optional)</h4>
                                            <SelectMulti
                                                id="flags-students"
                                                name="students"
                                                value={formik.values.students || []}
                                                onChange={formik.handleChange}
                                                options={safeStudentOptions.length > 0 ? safeStudentOptions : [{ value: -1, label: 'Loading students...' }]}
                                                placeholder="Select students to tie this flag to..."
                                                closeMenuOnSelect={false}
                                            />
                                            {formik.errors.students ? (
                                                <ErrorMessage color="mpLRed">
                                                    {formik.errors.students}
                                                </ErrorMessage>
                                            ) : null}
                                        </div>
                                    </div>

                                    <br />

                                    <div className="flex flex-row gap-x-4">
                                        <div className="grid grid-cols-1 gap-y-2 w-full">
                                            <h4>Notes</h4>
                                            <FormikControl
                                                id="flags-notes"
                                                name="notes"
                                                value={formik.values.notes}
                                                onChange={formik.handleChange}
                                                as="textarea"
                                                placeholder="Enter notes about the flag. Max 200 characters."
                                            />
                                            {formik.errors.notes ? (
                                                <ErrorMessage color="mpLRed">
                                                    {formik.errors.notes}
                                                </ErrorMessage>
                                            ) : null}
                                        </div>
                                    </div>

                                    <br />

                                    <div className="flex flex-row gap-x-4">
                                        <div className="grid grid-cols-1 gap-y-2 w-full">
                                            <h4>Follow-up Notes</h4>
                                            <FormikControl
                                                id="flags-followup-notes"
                                                name="followupNotes"
                                                value={formik.values.followupNotes}
                                                onChange={formik.handleChange}
                                                as="textarea"
                                                placeholder="Enter follow-up notes. Max 200 characters."
                                            />
                                            {formik.errors.followupNotes ? (
                                                <ErrorMessage color="mpLRed">
                                                    {formik.errors.followupNotes}
                                                </ErrorMessage>
                                            ) : null}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                // MathPlex form fields
                                <>
                                    <div className="flex flex-row gap-x-4">
                                        <div className="grid grid-cols-1 gap-y-2 w-full">
                                            <h4>Student</h4>
                                            <SelectSingle
                                                id="flags-student"
                                                name="student"
                                                value={formik.values.student}
                                                onChange={(e) => {
                                                    const s = e.target.value;
                                                    const newValues = { ...formik.values };
                                                    newValues.student = s;
                                                    // Use correct center field based on portal
                                                    const studentCenter = s.obj?.mp_primary_center;
                                                    newValues.center = getInitCenterOption(studentCenter, safeCenterOptions);
                                                    formik.setValues(newValues);
                                                }}
                                                options={studentOptions}
                                            />
                                            {formik.errors.student ? (
                                                <ErrorMessage color="mpLRed">
                                                    {formik.errors.student}
                                                </ErrorMessage>
                                            ) : null}
                                        </div>
                                    </div>

                                    <br />

                                    <div className="flex flex-row gap-x-4 items-start">
                                        <div className="grid grid-cols-1 gap-y-2 w-1/2">
                                            <h4>Tag</h4>
                                            <SelectSingle
                                                id="flags-tag"
                                                name="tag"
                                                value={formik.values.tag}
                                                onChange={formik.handleChange}
                                                options={tagOptions}
                                            />
                                            {formik.errors.tag ? (
                                                <ErrorMessage color="mpLRed">
                                                    {formik.errors.tag}
                                                </ErrorMessage>
                                            ) : null}
                                        </div>
                                        <div className="grid grid-cols-1 gap-y-2 w-1/2">
                                            <h4>Center</h4>
                                            <SelectSingle
                                                id="flags-center"
                                                name="center"
                                                value={formik.values.center}
                                                onChange={formik.handleChange}
                                                options={safeCenterOptions.length > 0 ? safeCenterOptions : [{ value: -1, label: 'Loading centers...' }]}
                                            />
                                            {formik.errors.center ? (
                                                <ErrorMessage color="mpLRed">
                                                    {formik.errors.center}
                                                </ErrorMessage>
                                            ) : null}
                                        </div>
                                    </div>

                                    <br />

                                    <div className="flex flex-row gap-x-4">
                                        <div className="grid grid-cols-1 gap-y-2 w-full">
                                            <h4>Additional Notes</h4>
                                            <FormikControl
                                                id="flags-additional-notes"
                                                name="additionalNotes"
                                                value={formik.values.additionalNotes}
                                                onChange={formik.handleChange}
                                                as="textarea"
                                                placeholder="Extra comments about the flag in case the tag doesn't suffice. Max 200 characters."
                                            />
                                            {formik.errors.additionalNotes ? (
                                                <ErrorMessage color="mpLRed">
                                                    {formik.errors.additionalNotes}
                                                </ErrorMessage>
                                            ) : null}
                                        </div>
                                    </div>

                                    <br />

                                    <div className="flex flex-row gap-x-4">
                                        <div className="grid grid-cols-1 gap-y-2 w-full">
                                            <h4>Admin Notes</h4>
                                            <FormikControl
                                                id="flags-admin-notes"
                                                name="adminNotes"
                                                value={formik.values.adminNotes}
                                                onChange={formik.handleChange}
                                                as="textarea"
                                                placeholder="Notes to be left by the person handling the flag. Max 200 characters."
                                            />
                                            {formik.errors.adminNotes ? (
                                                <ErrorMessage color="mpLRed">
                                                    {formik.errors.adminNotes}
                                                </ErrorMessage>
                                            ) : null}
                                        </div>
                                    </div>

                                    <br />

                                    <div className="flex flex-row gap-x-4">
                                        <div className="grid grid-cols-1 gap-y-2 w-full">
                                            <h4>Status</h4>
                                            <SelectSingle
                                                id="flags-status"
                                                name="status"
                                                value={formik.values.status}
                                                onChange={formik.handleChange}
                                                options={statusOptions}
                                            />
                                            {formik.errors.status ? (
                                                <ErrorMessage color="mpLRed">
                                                    {formik.errors.status}
                                                </ErrorMessage>
                                            ) : null}
                                        </div>
                                    </div>
                                </>
                            )}

                            {renderMeta(selectedFlag)}
                        </Modal.Body>
                        <Modal.Footer>
                            <div className="flex flex-row gap-x-2 flex-wrap">
                                {!formik.isValid && parseInt(formik.submitCount) && !formik.isSubmitting ?
                                    (
                                        <div className="text-mpLRed">
                                            One or more fields needs to be corrected.
                                        </div>
                                    ) : null
                                }
                                {formik.isSubmitting ?
                                    <ModalProcessing /> : null
                                }
                                {formik.status && !formik.isSubmitting ?
                                    <div className="text-mpLRed">
                                        {formik.status}
                                    </div> : null
                                }
                                <Button
                                    color="lte-mpLRed"
                                    disabled={formik.isSubmitting}
                                    onClick={handleClose}
                                >
                                    Close
                                </Button>
                                <Button
                                    color="lte-mpLBlue"
                                    onClick={formik.handleSubmit}
                                    disabled={formik.isSubmitting}
                                >
                                    Submit
                                </Button>
                            </div>
                        </Modal.Footer>
                    </ModalBodyFooter>
                )}
            </Formik>
        );
    }
}

// Set displayName and _modalComponentType on the CONNECTED component (not the original)
// Redux connect() returns a NEW component that doesn't preserve displayName
const ConnectedFlagsBodyFooter = connect(null, {

})(FlagsBodyFooter);
ConnectedFlagsBodyFooter.displayName = "BodyFooter";
ConnectedFlagsBodyFooter._modalComponentType = "BodyFooter";

export default ConnectedFlagsBodyFooter;